import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  MessageSquare, 
  X, 
  Send, 
  Loader2, 
  Sparkles,
  Mic,
  Bot,
  User
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suggested commands
const SUGGESTED_COMMANDS = [
  "Cerca task BMW",
  "Filtra priorità alta",
  "Quanti task WIP ci sono?",
  "Vai al kanban",
  "Nuovo task",
  "Esporta PDF"
];

export default function AIChatPanel({ 
  onSearch, 
  onFilterPriority, 
  onFilterStatus, 
  onFilterSystem,
  onFilterCountry,
  onClearFilters,
  onCreateTask,
  onExportPdf,
  isOpen,
  onClose
}) {
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    { 
      role: "assistant", 
      content: "Ciao! Sono l'assistente AI di TaskCommand. Scrivi un comando o scegli uno dei suggerimenti qui sotto.",
      timestamp: new Date()
    }
  ]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const processCommand = async (command) => {
    if (!command.trim()) return;
    
    // Add user message to chat
    const userMessage = { 
      role: "user", 
      content: command,
      timestamp: new Date()
    };
    setChatHistory(prev => [...prev, userMessage]);
    setMessage("");
    setIsProcessing(true);
    
    try {
      // Send command to AI backend
      const response = await axios.post(`${API}/ai/voice-command`, {
        command: command
      });
      
      const { action, params, message: aiMessage, success } = response.data;
      
      // Add AI response to chat
      const assistantMessage = {
        role: "assistant",
        content: aiMessage || "Comando elaborato",
        action: action,
        params: params,
        success: success,
        timestamp: new Date()
      };
      setChatHistory(prev => [...prev, assistantMessage]);
      
      if (!success) {
        return;
      }
      
      // Execute the action
      switch (action) {
        case "search":
          if (params.query) {
            onSearch?.(params.query);
            toast.success(`Ricerca: "${params.query}"`);
          }
          break;
          
        case "filter":
          if (params.priority) {
            onFilterPriority?.(params.priority);
          }
          if (params.status) {
            onFilterStatus?.(params.status);
          }
          if (params.system) {
            onFilterSystem?.(params.system);
          }
          if (params.country) {
            onFilterCountry?.(params.country);
          }
          toast.success("Filtri applicati");
          break;
          
        case "navigate":
          if (params.destination) {
            const routes = {
              dashboard: '/dashboard',
              tasks: '/tasks',
              kanban: '/kanban',
            };
            navigate(routes[params.destination] || '/dashboard');
            toast.success(`Navigazione: ${params.destination}`);
          }
          break;
          
        case "create":
          onCreateTask?.(params.info || "");
          break;
          
        case "export":
          onExportPdf?.();
          break;
          
        case "clear":
          onClearFilters?.();
          toast.success("Filtri rimossi");
          break;
          
        case "info":
          // Info already shown in chat
          break;
          
        default:
          break;
      }
      
    } catch (error) {
      console.error('AI command error:', error);
      const errorMessage = {
        role: "assistant",
        content: "Mi dispiace, c'è stato un errore. Riprova.",
        success: false,
        timestamp: new Date()
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    processCommand(message);
  };

  const handleSuggestionClick = (suggestion) => {
    processCommand(suggestion);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-20 right-4 md:right-6 w-[calc(100%-2rem)] md:w-[400px] max-h-[500px] bg-card border border-border rounded-xl shadow-2xl z-50 flex flex-col animate-in slide-in-from-bottom-5 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/30 rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Assistente AI</h3>
            <p className="text-xs text-muted-foreground">Scrivi un comando</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px] max-h-[300px]">
        {chatHistory.map((msg, index) => (
          <div 
            key={index}
            className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Bot className="w-3 h-3 text-primary" />
              </div>
            )}
            <div 
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : msg.success === false 
                    ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                    : 'bg-secondary'
              }`}
            >
              {msg.content}
              {msg.action && msg.action !== 'unknown' && msg.role === 'assistant' && (
                <div className="mt-1 pt-1 border-t border-border/50">
                  <span className="text-[10px] text-muted-foreground">
                    Azione: {msg.action}
                  </span>
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1">
                <User className="w-3 h-3" />
              </div>
            )}
          </div>
        ))}
        
        {isProcessing && (
          <div className="flex gap-2 justify-start">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-3 h-3 text-primary" />
            </div>
            <div className="bg-secondary rounded-lg px-3 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Suggestions */}
      {chatHistory.length <= 2 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-muted-foreground mb-2">Suggerimenti:</p>
          <div className="flex flex-wrap gap-1">
            {SUGGESTED_COMMANDS.map((cmd, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(cmd)}
                disabled={isProcessing}
                className="text-xs px-2 py-1 rounded-full bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
              >
                {cmd}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Scrivi un comando..."
            disabled={isProcessing}
            className="flex-1 bg-secondary h-10"
            data-testid="ai-chat-input"
          />
          <Button 
            type="submit" 
            size="icon" 
            disabled={isProcessing || !message.trim()}
            className="h-10 w-10"
            data-testid="ai-chat-send"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
